/* eslint-disable @typescript-eslint/no-explicit-any */
import {Alert, Box, BoxProps, Tooltip, Typography} from '@mui/material';
import Panels from 'components/Panels/Panels';
import BlankCard from 'components/BlankCard/BlankCard';
import {FC, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useLazyQuery} from '@apollo/client';
import {
  GET_MONETIZATION_BALANCE,
  GET_MONETIZATION_CONFIGURATIONS,
} from 'graphql/absences/Querys';
import {authContext} from 'contextApi/context/auth';
import {formatDateByLocale} from 'commons/formatDate';

export interface MonetizedDaysModel {
  availableDays?: number;
  usedDays?: number;
  expirationDate?: string | null;
}

export interface MonetizedDaysBalanceProps extends BoxProps {
  onBalanceLoaded?: (balance: MonetizedDaysModel) => void;
}

const MonetizedDaysBalance: FC<MonetizedDaysBalanceProps> = ({
  className,
  onBalanceLoaded,
}) => {
  const {t, i18n} = useTranslation();
  const {userDetails} = useContext(authContext);
  const [balance, setBalance] = useState<MonetizedDaysModel | null>(null);
  const [hasMonetizationRules, setHasMonetizationRules] = useState<
    boolean | null
  >(null);

  const [fetchBalance] = useLazyQuery(GET_MONETIZATION_BALANCE);
  const [fetchMonetizationConfigs] = useLazyQuery(
    GET_MONETIZATION_CONFIGURATIONS
  );

  useEffect(() => {
    const country = userDetails?.data?.country;
    if (!country) return;

    // Verificar si el país tiene reglas de monetización activas
    fetchMonetizationConfigs({
      variables: {country, status: 'ACTIVE'},
    })
      .then(({data}) => {
        const configs = data?.getMonetizationConfigurations?.data || [];
        const hasActiveConfigs = configs.length > 0;
        setHasMonetizationRules(hasActiveConfigs);
      })
      .catch(() => {
        setHasMonetizationRules(false);
      });
  }, [userDetails?.data?.country, fetchMonetizationConfigs]);

  useEffect(() => {
    const userId = userDetails?.data?.id;
    const country = userDetails?.data?.country;
    if (!userId || !country || !hasMonetizationRules) return;

    fetchBalance({variables: {userId, country}})
      .then(({data}) => {
        const result: MonetizedDaysModel = {
          availableDays: data?.getMonetizationBalance?.availableDays ?? 0,
          usedDays: data?.getMonetizationBalance?.usedDays ?? 0,
          expirationDate: data?.getMonetizationBalance?.expirationDate ?? null,
        };
        setBalance(result);
        onBalanceLoaded?.(result);
      })
      .catch(() => {
        setBalance({availableDays: 0, usedDays: 0, expirationDate: null});
      });
  }, [
    userDetails?.data?.id,
    userDetails?.data?.country,
    hasMonetizationRules,
    fetchBalance,
    onBalanceLoaded,
  ]);

  // No mostrar el componente si no hay reglas de monetización
  if (hasMonetizationRules === false) return null;
  if (!balance) return null;

  const expirationDate = balance.expirationDate
    ? new Date(balance.expirationDate)
    : null;
  const hasExpirationDate =
    expirationDate !== null && !Number.isNaN(expirationDate.getTime());
  const expirationDateLabel = hasExpirationDate
    ? formatDateByLocale(
        expirationDate,
        i18n.language as 'en' | 'es' | 'fr' | 'pt'
      )
    : '';

  const isExpiringSoon = (() => {
    if (!hasExpirationDate || !expirationDate) return false;
    const now = new Date();
    const oneMonthFromNow = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      now.getDate()
    );
    return expirationDate <= oneMonthFromNow && expirationDate > now;
  })();

  return (
    <Box
      className={`MonetizedDaysBalance ${className ?? ''}`}
      sx={{
        flex: 'flex',
        flexFlow: 'row',
        justifyContent: 'stretch',
        alignItems: 'center',
      }}
    >
      <BlankCard
        className="p-0"
        title={
          <div className="flex justify-end">
            <Tooltip title={t('leaves.MonetizedDaysBalance.tooltip')}>
              <InfoOutlinedIcon className="cursor-pointer" />
            </Tooltip>
          </div>
        }
      >
        <Panels
          panelProps={{className: 'h-full'}}
          items={[
            <div
              key="available"
              className="flex flex-col justify-center items-center"
            >
              <Typography variant="h2">{balance.availableDays ?? 0}</Typography>
              <Typography variant="caption">
                {t(
                  'leaves.MonetizedDaysBalance.available_days_label',
                  'Días Monetizados'
                )}
              </Typography>
            </div>,
            <div key="used" className="flex h-full flex-col justify-center">
              <div className="flex items-center">
                <Typography variant="h5">{balance.usedDays ?? 0}</Typography>
                <Typography variant="caption" sx={{ml: 1}}>
                  {t(
                    'leaves.MonetizedDaysBalance.used_days_label',
                    'Días pagados'
                  )}
                </Typography>
              </div>
            </div>,
          ]}
        />
        {isExpiringSoon && (balance.availableDays ?? 0) > 0 && (
          <>
            <hr className="my-2" />
            <Alert
              severity="warning"
              icon={<InfoIcon />}
              className="mt-2"
              sx={{alignItems: 'center'}}
            >
              {t(
                'leaves.notice_expire_message',
                'Aviso: Tienes {{days}} días de {{type}} a punto de expirar. Solicítalos antes del {{date}}',
                {
                  days: Math.floor((balance.availableDays ?? 0) * 100) / 100,
                  type: t('leaves.MONETIZATION', 'Días monetizados'),
                  date: expirationDateLabel,
                }
              )}
            </Alert>
          </>
        )}
      </BlankCard>
    </Box>
  );
};

export default MonetizedDaysBalance;
